import { Injectable } from '@angular/core';
import { ProductType } from '@core/models/api/dsm-types';
import {
  DownPaymentExperienceResponse,
  EligiblePayplansAndPayMethods,
} from '@core/models/api/response/down-payment-experience-response.model';
import {
  PaymentType,
  DownPaymentDetails,
  PaymentPlanType,
  PaymentMethod,
  Surcharge,
  Fee,
  PayBindPageModel,
} from '@core/models/views/pay-bind-page-model';
import { PremiumEntity } from '@entities/premium/premium.entity';
import { ProductModel } from '@entities/product/product.model';
import { DateUtils } from '@shared/utils/date.utils';
import {
  Observable,
  catchError,
  combineLatest,
  concatMap,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ProductsByBillingAccount } from './billing.service';
import { ProductsService } from './products.service';
import { MortgageService } from './mortgage.service';
import { PremiumService } from './premium.service';
import { Store } from '@ngrx/store';
import * as BillingSelectors from '@core/store/entities/billing/billing.selector';
import { getPaymentMethods } from '@forms-store/store/models/pay-bind/pay-bind.selector';
import { PayBindSelectors } from '@forms-store/store/selectors';
import { PayBindActions } from '@forms-store/store/actions';
import {
  BillingAdapter,
  PaymentechProfileRequest,
  PaymentechProfileResponse,
} from '@core/adapters/billing.adapter';
import { SetupAccountExperienceRequest } from '@core/models/api/request/setup-account-experience-request.model';
import { MemberEntity } from '@entities/member/member.reducer';
import {
  AgencySelectors,
  MemberSelectors,
  ProductsSelectors,
  UserSelectors,
} from '@core/store/selectors';
import { AgencyModel } from '@entities/agency/agency.model';
import { PolicyNumberService } from './policy-number.service';
import { SetupAccountExperienceResponse } from '@core/models/api/response/setup-account-experience-response.model';
import { MortgageEntity } from '@entities/mortgage/mortgage.reducer';
import { SaeRequestService } from './sae-request.service';
import { PoliciesAndQuotes } from '@core/models/api/request/down-payment-experience-request.model';
import { BillingAccountActions } from '@core/store/actions';
import { ErrorSanitizerService } from './error-sanitizer.service';
import { addError } from '@entities/error/error.action';

export interface SubmitPaymentInputs {
  payBindPage: PayBindPageModel;
  agencyModel: AgencyModel;
  isNss: boolean;
  members: MemberEntity[];
  downPayments: DownPaymentExperienceResponse[];
  products: ProductModel[];
  mortgages: MortgageEntity[];
}

export interface PaymentResult {
  policiesAndQuotes: PoliciesAndQuotes[];
  response?: SetupAccountExperienceResponse;
  error?: any;
}

@Injectable({
  providedIn: 'root',
})
export class PayBindService {
  constructor(
    private productsService: ProductsService,
    private premiumService: PremiumService,
    private mortgageService: MortgageService,
    private store: Store,
    private billingAdapter: BillingAdapter,
    private policyNumberService: PolicyNumberService,
    private saeRequestService: SaeRequestService,
    private errorSanitizerService: ErrorSanitizerService
  ) {}

  /**
   * Returns one entry for each billing account that ought to be set up.
   * Up to three: Escrow, regular Billing, Auto FullPay Billing.
   */
  getProductsByBillingAccount(): Observable<ProductsByBillingAccount[]> {
    return this.productsService.getSelectedProducts().pipe(
      map((products) => {
        const accounts: ProductsByBillingAccount[] = [];
        const uniquePaymentPlans = Array.from(
          new Set(products.map((p) => p.paymentPlan).filter(Boolean))
        );

        uniquePaymentPlans.forEach((paymentPlan) => {
          if (paymentPlan === 'ESCROW') {
            const escrowProductTypes = products
              ?.filter((product) => product.paymentPlan === 'ESCROW')
              ?.map((product) => product.type);
            for (const productType of escrowProductTypes) {
              accounts.push({
                billingAccountType: 'billing', // Escrow accounts are now managed by SAE
                productIds: [productType],
              });
            }
          } else if (paymentPlan === 'PAY IN FULL') {
            const productTypesWithPaymentPlan = products
              .filter((product) => product.paymentPlan === paymentPlan)
              ?.map((product) => product.type);
            for (const productType of productTypesWithPaymentPlan) {
              accounts.push({
                billingAccountType: 'billing',
                productIds: [productType],
              });
            }
          } else {
            const productTypesWithPaymentPlan = products
              .filter((product) => product.paymentPlan === paymentPlan)
              ?.map((product) => product.type);
            if (productTypesWithPaymentPlan?.length) {
              accounts.push({
                billingAccountType: 'billing',
                productIds: productTypesWithPaymentPlan,
              });
            }
          }
        });

        return accounts;
      })
    );
  }

  getPaymentTypes(): Observable<PaymentType[]> {
    return combineLatest([
      this.productsService.getSelectedProducts(),
      this.productsService.getSelectedProductTypes(),
      this.premiumService.getAllPremiums(),
      this.mortgageService.getAllMortgages(),
      this.getAllDownPayments(),
    ]).pipe(
      map(([products, productTypes, premiums, mortgages, downPayments]) => {
        const paymentTypes: PaymentType[] = [];
        const uniquePaymentPlans = Array.from(
          new Set(products.map((p) => p.paymentPlan).filter(Boolean))
        );

        uniquePaymentPlans.forEach((paymentPlan) => {
          if (paymentPlan === 'ESCROW') {
            const escrowProducts = products?.filter(
              (product) => product.paymentPlan === paymentPlan
            );
            if (escrowProducts && escrowProducts.length > 0) {
              const productBillingDetails = escrowProducts.map((escrowProduct) => {
                const escrowProductPremium = premiums.find(
                  (prem) => prem.productType === escrowProduct.type
                );
                const productMortgage = mortgages.find(
                  (mtg) => mtg.productType === escrowProduct.type
                );
                return {
                  productType: escrowProduct.type,
                  name: escrowProduct.name || 'PersonalAuto',
                  imageUrl: escrowProduct.imageUrl || '',
                  totalPremium: escrowProductPremium?.total?.amount || 0,
                  premiumTerm: escrowProductPremium?.termMonths || 0,
                  quoteId: escrowProduct.quoteId || '',
                  mortgage: productMortgage,
                  effectiveDate: escrowProduct.effectiveDate || '',
                };
              });
              
              paymentTypes.push({
                paymentPlan,
                products: productBillingDetails,
              });
            }
          } else if (paymentPlan === 'PAY IN FULL') {
            const productsWithPaymentPlan = products.filter(
              (product) => product.paymentPlan === paymentPlan
            );
            for (const product of productsWithPaymentPlan) {
              const productPremium = premiums.find(
                (prem) => prem.productType === product.type
              );
              paymentTypes.push({
                paymentPlan,
                products: [
                  {
                    productType: product.type,
                    name: product.name || 'PersonalAuto',
                    imageUrl: product.imageUrl || '',
                    totalPremium: productPremium?.total?.amount || 0,
                    premiumTerm: productPremium?.termMonths || 0,
                    quoteId: product.quoteId || '',
                    effectiveDate: product.effectiveDate || '',
                  },
                ],
                downPaymentDetails: this.getDownPaymentDetails(
                  paymentPlan,
                  downPayments,
                  Array(product),
                  productTypes,
                  premiums
                ),
                paymentMethods: this.getPaymentMethods(paymentPlan, product),
              });
            }
          } else {
            const productsWithPaymentPlan = products.filter(
              (product) => product.paymentPlan === paymentPlan
            );
            const productBillingDetails = [];
            for (const product of productsWithPaymentPlan) {
              const productPremium = premiums.find(
                (prem) => prem.productType === product.type
              );
              productBillingDetails.push({
                productType: product.type,
                name: product.name || 'PersonalAuto',
                imageUrl: product.imageUrl || '',
                totalPremium: productPremium?.total?.amount || 0,
                premiumTerm: productPremium?.termMonths || 0,
                quoteId: product.quoteId || '',
                effectiveDate: product.effectiveDate || '',
              });
            }
            if (
              !!paymentPlan &&
              (paymentPlan === 'MONTHLY DIRECT' ||
                paymentPlan === 'MONTHLY EFT' ||
                paymentPlan == 'MONTHLY RECURRING BANKCARD')
            ) {
              paymentTypes.push({
                paymentPlan,
                products: productBillingDetails,
                downPaymentDetails: this.getDownPaymentDetails(
                  paymentPlan,
                  downPayments,
                  productsWithPaymentPlan,
                  productTypes,
                  premiums
                ),
                paymentMethods: this.getPaymentMethods(
                  paymentPlan,
                  productsWithPaymentPlan[0]
                ),
              });
            }
          }
        });

        return paymentTypes;
      })
    );
  }

  getAllDownPayments(): Observable<DownPaymentExperienceResponse[]> {
    return this.store.select(BillingSelectors.getAllDownPayments);
  }

  private addSurchargesToDownPaymentDetails(
    downPaymentDetails: DownPaymentDetails,
    productTypes: ProductType[],
    premiums: PremiumEntity[]
  ): void {
    for (const productType of productTypes) {
      const premium = premiums.find((p) => p.productType === productType);
      if (!!premium?.surcharges?.length) {
        for (const surcharge of premium.surcharges) {
          const existingSurchargeIndex =
            downPaymentDetails.surcharges?.findIndex(
              (s) => s.description === surcharge.description
            );
          if (existingSurchargeIndex > -1) {
            const existingSurcharge =
              downPaymentDetails.surcharges[existingSurchargeIndex];
            downPaymentDetails.surcharges[existingSurchargeIndex] = {
              description: existingSurcharge.description,
              amount: existingSurcharge.amount + surcharge.amount,
            };
          } else {
            downPaymentDetails.surcharges.push({
              description: surcharge.description || 'Surcharge fee',
              amount: surcharge.amount,
            });
          }
        }
      }
    }
  }

  private getDownPaymentDetails(
    paymentPlan: string,
    downPayments: DownPaymentExperienceResponse[],
    products: ProductModel[],
    productTypes: ProductType[],
    premiums: PremiumEntity[]
  ): DownPaymentDetails | undefined {
    const downPayment = downPayments.find((dp) => {
      if (dp.policyLevel.length !== products.length) {
        return false;
      }
      for (const pl of dp.policyLevel) {
        const product = products.find(
          (p) =>
            p.quoteId === pl.displayPolicyNumber ||
            p.policyNumber === pl.displayPolicyNumber
        );
        if (!product) {
          return false;
        }
      }
      return true;
    });
    if (!downPayment) {
      return;
    }
    const payPlan = this.selectPayPlan(downPayment, paymentPlan);
    if (!payPlan) {
      return;
    }
    const downPaymentDetails = {
      fees: this.getFees(productTypes, premiums, payPlan, paymentPlan),
      surcharges: this.getSurcharges(productTypes, premiums),
      downPaymentAmount: this.billingAmountAsNumber(
        payPlan.totalDownPaymentWithFees
      ),
      fullPayAmount: this.billingAmountAsNumber(payPlan.fullPayAmount),
      nextBillingAmount: this.billingAmountAsNumber(
        payPlan.accountNextBillAmountNoFee
      ),
      nextBillingDate: DateUtils.formatDsmDateToOld(
        payPlan.accountNextBillDate
      ),
    };
    this.addSurchargesToDownPaymentDetails(
      downPaymentDetails,
      productTypes,
      premiums
    );
    return downPaymentDetails;
  }

  private getFees(
    productTypes: ProductType[],
    premiums: PremiumEntity[],
    payPlan: EligiblePayplansAndPayMethods,
    paymentPlan: string
  ): Fee[] {
    const fees: Fee[] = [];
    for (const productId of productTypes) {
      const premium = premiums.find((p) => p.productType === productId);
      if (premium?.fees?.amount) {
        fees.push({
          description: 'Policy fee',
          amount: premium.fees.amount,
        });
      }
    }

    if (paymentPlan !== 'PAY IN FULL') {
      const monthlyFee = this.billingAmountAsNumber(payPlan.monthlyFees);
      if (monthlyFee) {
        fees.push({
          description: 'Fee per payment',
          amount: monthlyFee,
        });
      }
    }

    return fees;
  }

  private getSurcharges(
    productTypes: ProductType[],
    premiums: PremiumEntity[]
  ): Surcharge[] {
    const surcharges: Surcharge[] = [];
    for (const productType of productTypes) {
      const premium = premiums.find((p) => p.productType === productType);
      if (!!premium?.surcharges?.length) {
        for (const surcharge of premium.surcharges) {
          const existingSurchargeIndex = surcharges.findIndex(
            (s) => s.description === surcharge.description
          );
          if (existingSurchargeIndex > -1) {
            const existingSurcharge = surcharges[existingSurchargeIndex];
            surcharges[existingSurchargeIndex] = {
              description: existingSurcharge.description,
              amount: existingSurcharge.amount + surcharge.amount,
            };
          } else {
            surcharges.push({
              description: surcharge.description || 'Surcharge fee',
              amount: surcharge.amount,
            });
          }
        }
      }
    }
    return surcharges;
  }

  private getPaymentMethods(
    paymentPlanType: PaymentPlanType,
    product: ProductModel
  ): PaymentMethod[] {
    let paymentMethods: PaymentMethod[] | undefined = [];
    this.store
      .select(getPaymentMethods(paymentPlanType, product.type))
      .pipe(take(1))
      .subscribe((payMethods) => (paymentMethods = payMethods));
    return paymentMethods;
  }

  private selectPayPlan(
    downPayment: DownPaymentExperienceResponse,
    paymentPlan: string
  ): EligiblePayplansAndPayMethods | undefined {
    const plans = downPayment.eligiblePayplansAndPayMethods;
    switch (paymentPlan) {
      case 'PAY IN FULL': {
        const monthlyDirects = plans.filter(
          (pp) => pp.payMethod === 'MONTHLY DIRECT'
        );
        // NB: "FULL PAYMENT PLAN" if present, otherwise "COMBINED MONTHLY". Don't combine these conditions.
        return (
          monthlyDirects.find((pp) => pp.payPlan === 'FULL PAYMENT PLAN') ||
          monthlyDirects.find((pp) => pp.payPlan === 'COMBINED MONTHLY')
        );
      }
      case 'MONTHLY DIRECT':
      case 'MONTHLY EFT':
      case 'MONTHLY RECURRING BANKCARD': {
        const monthlyPlans = plans.filter((pp) => pp.payMethod === paymentPlan);
        return (
          monthlyPlans.find((pp) => pp.payPlan === 'COMBINED MONTHLY') ||
          monthlyPlans.find((pp) => pp.payPlan === 'FULL PAYMENT PLAN')
        );
      }
    }
    return undefined;
  }

  private billingAmountAsNumber(input: string): number {
    if (!input) {
      return 0;
    }
    // NB it's not just spaces, commas too.
    const dotsAndDigits = input.replace(/[^0-9.]/g, '');
    return +dotsAndDigits || 0;
  }

  /**
   * Generates policy numbers and Paymentech tokens if needed.
   * Calls SetupAccountExperience as many times as needed.
   * Returns all SAE results.
   * Beware! If any call fails, the others proceed. You must check the result for errors.
   * It is also possible for the returned Observable to fail in the usual fashion, eg if Paymentech fails.
   * Core.billingAccount will be populated before we emit.
   */
  submitPayment(): Observable<PaymentResult[]> {
    return this.store.select(PayBindSelectors.getPayBindSubmitInProgress).pipe(
      take(1),
      switchMap((inProgress) => {
        if (inProgress) {
          return throwError(() => 'Payment already in progress.');
        }
        this.store.dispatch(
          PayBindActions.setSubmitInProgress({ submitInProgress: true })
        );
        return this.policyNumberService.generatePolicyNumberIfAbsent().pipe(
          take(1),
          switchMap(() => this.fetchSubmitPaymentInputs()),
          tap((inputs) => this.assertProductsInBindingStatus(inputs)),
          switchMap((inputs) => this.submitPaymentForStoreContent(inputs)),
          finalize(() => {
            this.store.dispatch(
              PayBindActions.setSubmitInProgress({ submitInProgress: false })
            );
          })
        );
      })
    );
  }

  private fetchSubmitPaymentInputs(): Observable<SubmitPaymentInputs> {
    return combineLatest([
      this.store.select(PayBindSelectors.getPayBindPage),
      this.store.select(AgencySelectors.getAgency),
      this.store.select(UserSelectors.isNssUser),
      this.store.select(MemberSelectors.getAllMembers),
      this.store.select(BillingSelectors.getAllDownPayments),
      this.store.select(ProductsSelectors.getSelectedProductEntities),
      this.mortgageService.getAllMortgages(),
    ]).pipe(
      take(1),
      map(
        ([
          payBindPage,
          agencyModel,
          isNss,
          members,
          downPayments,
          products,
          mortgages,
        ]) => ({
          payBindPage,
          agencyModel,
          isNss,
          members,
          downPayments,
          products,
          mortgages,
        })
      )
    );
  }

  private assertProductsInBindingStatus(inputs: SubmitPaymentInputs): void {
    for (const pbpProduct of inputs.payBindPage.products) {
      const product = inputs.products.find(p => p.type === pbpProduct.type);
      if (!product || (product.quoteStatus !== 'Binding')) {
        throw new Error(`Can't issue policies due to ${pbpProduct.name} in status ${product?.quoteStatus}`);
      }
    }
  }

  private submitPaymentForStoreContent(
    inputs: SubmitPaymentInputs
  ): Observable<PaymentResult[]> {
    const reqs = this.saeRequestService.composeSaeRequests(inputs);
    return this.createPaymentechProfilesForSaeRequests(reqs).pipe(
      concatMap((reqs) =>
        forkJoin(
          reqs.map((req) =>
            this.billingAdapter.setupAccount(req).pipe(
              map((response) => ({
                policiesAndQuotes: req.body.policiesAndQuotes,
                response,
              })),
              tap((result) =>
                this.updateStoreWithBillingAccount(inputs, req, result)
              ),
              catchError((error) => {
                error = this.errorSanitizerService.sanitizeError(
                  error || 'Failed to setup billing account.'
                );
                this.store.dispatch(
                  addError({
                    payload: this.errorSanitizerService.sanitizeError(error),
                  })
                );
                return of({
                  policiesAndQuotes: req.body.policiesAndQuotes,
                  error,
                });
              })
            )
          )
        )
      )
    );
  }

  private updateStoreWithBillingAccount(
    inputs: SubmitPaymentInputs,
    request: SetupAccountExperienceRequest,
    result: PaymentResult
  ): void {
    // policiesAndQuotes will never be null in real life, but defaulting it for unit tests' sake.
    const productIds: ProductType[] = (request.body.policiesAndQuotes || [])
      .map((pq) => {
        const product = inputs.products.find((p) => p.quoteId === pq.quoteId);
        if (product) return product.type;
        return null;
      })
      .filter((v) => v) as ProductType[];
    this.store.dispatch(
      BillingAccountActions.upsertBillingAccount({
        payload: {
          id: 0,
          status: 'ok',
          productIds,
          accountNumber: result.response!.accountNumber,
          receiptId: result.response!.receiptId,
        },
      })
    );
  }

  /* Anything in req that needs a Paymentech token, create the token.
   * After all calls succeed, emit a finished request, with all the new tokens present.
   * May rewrite (reqs) in place.
   */
  private createPaymentechProfilesForSaeRequests(
    reqs: SetupAccountExperienceRequest[]
  ): Observable<SetupAccountExperienceRequest[]> {
    const paymentechRequests: Observable<any>[] = [];
    for (let i = 0; i < reqs.length; i++) {
      const req = reqs[i];
      if (req.body.payment?.paymentTransaction?.bankCardWithClearPan) {
        const paymentechProfileRequest: PaymentechProfileRequest =
          this.buildPaymentechProfileRequest(req, false);
        paymentechRequests.push(
          this.billingAdapter
            .createPaymentechProfile(paymentechProfileRequest)
            .pipe(
              tap((paymentechResponse) => {
                reqs[i] = this.rewriteSaeRequestWithPaymentechResponse(
                  reqs[i],
                  paymentechResponse,
                  false
                );
              }),
              catchError((e) => {
                this.store.dispatch(
                  addError({
                    payload: this.errorSanitizerService.sanitizeError(e),
                  })
                );
                throw e;
              })
            )
        );
      }
      if ((req.body.recurringPayment as any)?.bankCardWithClearPan) {
        const paymentechProfileRequest = this.buildPaymentechProfileRequest(
          req,
          true
        );
        paymentechRequests.push(
          this.billingAdapter
            .createPaymentechProfile(paymentechProfileRequest)
            .pipe(
              tap((paymentechResponse) => {
                reqs[i] = this.rewriteSaeRequestWithPaymentechResponse(
                  reqs[i],
                  paymentechResponse,
                  true
                );
              })
            )
        );
      }
    }
    if (paymentechRequests.length) {
      return combineLatest(paymentechRequests).pipe(map(() => reqs));
    } else {
      return of(reqs);
    }
  }

  private buildPaymentechProfileRequest(
    saeRequest: SetupAccountExperienceRequest,
    forRecurring: boolean
  ): PaymentechProfileRequest {
    if (forRecurring) {
      const bcc = (saeRequest.body.recurringPayment as any)
        .bankCardWithClearPan;
      const pay = (saeRequest.body.recurringPayment as any).payer;
      return {
        cardholderName: pay.displayName,
        billingAddress1: pay.addressLine1,
        billingAddress2: pay.addressLine2,
        billingCity: pay.city,
        billingState: pay.state,
        billingZIP: pay.postalCode,
        accountType: 'CC',
        accountNumber: bcc.cardNumber,
        expirationDate: this.expirationDateYyyyMm(bcc.expirationDate),
      };
    } else {
      const bcc =
        saeRequest.body.payment.paymentTransaction!.bankCardWithClearPan!;
      const pay = saeRequest.body.payment.payer!;
      return {
        cardholderName: pay.displayName,
        billingAddress1: pay.addressLine1,
        billingAddress2: pay.addressLine2,
        billingCity: pay.city,
        billingState: pay.state,
        billingZIP: pay.postalCode,
        accountType: 'CC',
        accountNumber: bcc.cardNumber || '',
        expirationDate: this.expirationDateYyyyMm(bcc.expirationDate || ''),
      };
    }
  }

  private expirationDateYyyyMm(input: string): string {
    const splits = input.split('/');
    if (splits.length === 2) {
      // With a slash, it must be "MM/YY".
      const month = +splits[0];
      let year = +splits[1];
      if (year < 100) year += 2000;
      return `${year}${month.toString().padStart(2, '0')}`;
    }
    if (input.length === 4) {
      // 4 digits, assume "MMYY".
      const month = +input.substring(0, 2);
      const year = 2000 + +input.substring(2, 4);
      return `${year}${month.toString().padStart(2, '0')}`;
    }
    return input;
  }

  private rewriteSaeRequestWithPaymentechResponse(
    saeRequest: SetupAccountExperienceRequest,
    ptResponse: PaymentechProfileResponse,
    forRecurring: boolean
  ): SetupAccountExperienceRequest {
    if (ptResponse.status !== '0') {
      throw new Error(
        `Unexpected status from Paymentech: ${JSON.stringify(ptResponse)}`
      );
    }
    if (forRecurring) {
      const src = (saeRequest.body.recurringPayment as any)
        .bankCardWithClearPan!;
      saeRequest.body.recurringPayment = {
        bankCard: {
          cardBrand: src.cardType,
          expirationDate: this.expirationDateYyyyMm(src.expirationDate),
          ccLastFour: (src.cardNumber || '').substring(
            (src.cardNumber || '').length - 4
          ),
          profileId: ptResponse.profileRefNum,
          cardVerificationValue: src.cardVerificationValue,
        } as any,
        payer: saeRequest.body.recurringPayment!.payer,
      };
    } else {
      const src =
        saeRequest.body.payment.paymentTransaction!.bankCardWithClearPan!;
      saeRequest.body.payment.paymentTransaction = {
        paymentMethod: 'bankCard',
        bankCard: {
          cardBrand: src.cardType,
          expirationDate: this.expirationDateYyyyMm(src.expirationDate || ''),
          ccLastFour: (src.cardNumber || '').substring(
            (src.cardNumber || '').length - 4
          ),
          profileId: ptResponse.profileRefNum,
          cardVerificationValue: src.cardVerificationValue,
        },
      };
    }
    return saeRequest;
  }
}
